import axios from 'axios'
import router from '@/router'
import { sendOtp, verifyOtp } from '@/store/api'
import { te } from '@/locales'

let requestErrorTimeout = null

const defaultPassword = {
  required: false,
  title: null,
  incorrect: false,
  password: null,
  errorMessage: null,
  errorCode: 0
}

const defaultOtp = {
  required: false,
  verified: false,
  sessionUid: null,
  mobileLastFour: null,
  codeExpires: null,
  requestError: null,
  verifyError: null
}

const authModule = {
  namespaced: true,
  state: {
    isAuthenticated: false,
    authAttempted: false,
    user: {},
    password: { ...defaultPassword },
    otp: {
      ...defaultOtp,
      codesSent: 0
    }
  },
  mutations: {
    SET_AUTHENTICATED (state) {
      state.isAuthenticated = true
    },
    SET_AUTH_ATTEMPTED (state) {
      state.authAttempted = true
    },
    SET_USER (state, user) {
      state.user = user
    },
    RESET_PASSWORD (state) {
      state.password = { ...defaultPassword }
    },
    SET_PASSWORD (state, { required, title, incorrect, password, errorMessage, errorCode }) {
      state.password = { required, title, incorrect, password, errorMessage, errorCode }
    },
    SET_OTP_SESSION (state, { sessionUid, mobileLastFour }) {
      state.otp = {
        ...state.otp,
        required: true,
        verified: false,
        sessionUid,
        mobileLastFour
      }

      axios.defaults.headers.common['X-Session-Uid'] = sessionUid
    },
    SET_OTP_NOT_VERIFIED (state) {
      state.otp = {
        ...state.otp,
        required: true,
        verified: false
      }
    },
    SET_CODE_SENT (state, expires) {
      state.otp = {
        ...state.otp,
        codeExpires: expires,
        codesSent: state.otp.codesSent + 1
      }
    },
    CLEAR_OTP_ERRORS (state) {
      state.otp = {
        ...state.otp,
        requestError: null,
        verifyError: null
      }
    },
    SET_OTP_REQUEST_ERROR (state, error) {
      state.otp = {
        ...state.otp,
        requestError: error
      }
    },
    SET_OTP_VERIFY_ERROR (state, error) {
      state.otp = {
        ...state.otp,
        verifyError: error
      }
    },
    SET_VERIFIED (state) {
      state.otp = {
        ...state.otp,
        verified: true,
        requestError: null,
        verifyError: null
      }
    }
  },
  actions: {
    SET_ACCESS_TOKEN_EXPIRY ({ commit }, value) {
      const expiry = new Date()
      expiry.setSeconds(expiry.getSeconds() + value.expires_in)
      sessionStorage.setItem('access_token_expiry', expiry.getTime())
    },
    SET_ACCESS_TOKEN ({ commit }, value) {
      sessionStorage.setItem('access_token', JSON.stringify(value))
    },
    REMOVE_ACCESS_TOKEN () {
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('access_token_expiry')
    },
    SET_AUTHENTICATION_VALUES ({ commit, state }, accessToken) {
      commit('SET_AUTHENTICATED')
      commit('SET_AUTH_ATTEMPTED')
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      if (state.otp.sessionUid) {
        axios.defaults.headers.common['X-Session-Uid'] = state.otp.sessionUid
      }
    },
    GET_USER ({ commit }) {
      return axios({
        method: 'get',
        baseURL: process.env.VUE_APP_API_URL,
        url: 'user'
      }).then((response) => {
        commit('SET_USER', response.data.data)
      }).catch(() => {})
    },
    AUTHENTICATE ({ commit, dispatch }, key) {
      return axios({
        method: 'post',
        baseURL: process.env.VUE_APP_API_URL,
        url: '/login/sign',
        data: {
          key
        }
      }).then((response) => {
        dispatch('SET_AUTHENTICATION_VALUES', response.data.data.access_token)
        dispatch('SET_ACCESS_TOKEN', response.data.data)
        dispatch('SET_ACCESS_TOKEN_EXPIRY', response.data.data)
      }).catch((error) => {
        sessionStorage.removeItem('access_token')
        commit('SET_AUTH_ATTEMPTED')
        const errorResponse = {
          errorMessage: (error.response && error.response.data.message) || error.message,
          errorCode: (error.response && error.response.status) || 0
        }

        router.push({ name: 'PageError', params: errorResponse })
      })
    },
    AUTHENTICATE_PREVIEW ({ commit }, key) {
      return axios({
        method: 'post',
        baseURL: process.env.VUE_APP_API_URL,
        url: '/login/preview',
        data: {
          key
        }
      }).then((response) => {
        const { data: { data: { access_token: token } } } = response
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        commit('SET_AUTHENTICATED')
        commit('SET_AUTH_ATTEMPTED')
      }).catch((error) => {
        commit('SET_AUTH_ATTEMPTED')
        const errorResponse = {
          errorMessage: (error.response && error.response.data.message) || error.message,
          errorCode: (error.response && error.response.status) || 0
        }

        router.push({ name: 'PageError', params: errorResponse })
      })
    },
    async SEND_OTP ({ commit }) {
      commit('CLEAR_OTP_ERRORS')

      try {
        const response = await sendOtp()
        commit(
          'SET_CODE_SENT',
          response && response.data && response.data.expires ? new Date(response.data.expires) : null
        )
        return true
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message &&
          te(error.response.data.message)
        ) {
          commit('SET_OTP_REQUEST_ERROR', error.response.data.message)
        } else if (error.response.status === 429) {
          commit('SET_OTP_REQUEST_ERROR', 'otp.rateLimitExceeded')
        } else {
          commit('SET_OTP_REQUEST_ERROR', 'otp.unknownError')
        }

        clearTimeout(requestErrorTimeout)
        // Clear error after 20 seconds
        requestErrorTimeout = setTimeout(() => {
          commit('SET_OTP_REQUEST_ERROR', null)
        }, 20000)

        return false
      }
    },
    async VERIFY_OTP ({ commit }, otpCode) {
      commit('CLEAR_OTP_ERRORS')

      try {
        const response = await verifyOtp(otpCode)
        commit('SET_VERIFIED')
        return response
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message &&
          te(error.response.data.message)
        ) {
          commit('SET_OTP_VERIFY_ERROR', error.response.data.message)
        } else if (error.response.status === 429) {
          commit('SET_OTP_REQUEST_ERROR', 'otp.rateLimitExceeded')
        } else {
          commit('SET_OTP_REQUEST_ERROR', 'otp.unknownError')
        }

        clearTimeout(requestErrorTimeout)
        // Clear error after 20 seconds
        requestErrorTimeout = setTimeout(() => {
          commit('SET_OTP_REQUEST_ERROR', null)
        }, 20000)

        return false
      }
    }
  },
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    authAttempted: state => state.authAttempted,
    user: state => state.user,
    passwordStatus: state => state.password,
    otpStatus: state => ({
      ...state.otp,
      showDialog: state.otp.required && !state.otp.verified
    })
  }
}

export default authModule
